import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { ListsOwnersError } from '../../../trustAdmin-api/generated-src';
import TrustAdminApiFactory from '../../../trustAdmin-api/TrustAdminApiFactory';
import { INVALID_FORM_INPUT_CHARACTERS_REGEX } from '../../../utils/validation';
import { ListSpamWorkflowFormView } from './ListSpamWorkflowFormView';

export interface ListSpamWorkflowFormInput {
    ids?: string[];
}

export interface ListSpamWorkflowFormResponseData {
    fetching?: boolean;
    error?: AxiosError;
    progress?: { processing: number; total: number };
}

const BATCH_SIZE = 100;

const validateFreeText = (customerIds: string[]) => !INVALID_FORM_INPUT_CHARACTERS_REGEX.test(customerIds.join(''));

export const ListSpamWorkflowForm: React.VFC = () => {
    const navigate = useNavigate();
    const [inputData, setInputData] = useState<ListSpamWorkflowFormInput>({});
    const [responseData, setResponseData] = useState<ListSpamWorkflowFormResponseData>({ fetching: false });
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const validateInput = (input: ListSpamWorkflowFormInput) => {
        if (!input?.ids || input.ids.length === 0) {
            setResponseData({ error: new Error('Please ensure you have entered IDs to look up.') as AxiosError });
        } else if (validateFreeText(input.ids)) {
            setResponseData({
                error: new Error('Entered IDs do not match the correct format. Please fix.') as AxiosError
            });
        } else {
            setIsModalOpen(true);
        }
    };

    const onInputChange = (newData: object) => {
        setInputData({ ...inputData, ...newData });
    };

    const onSubmit = async () => {
        setIsModalOpen(false);
        const totalIds = inputData.ids!.length;
        setResponseData({ fetching: true, progress: { processing: 0, total: totalIds } });
        try {
            const ids = inputData.ids!;
            const allCustomerIds: Set<string> = new Set<string>();
            const allErrors: ListsOwnersError[] = [];

            for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                const batch = ids.slice(i, i + BATCH_SIZE);

                setResponseData({
                    fetching: true,
                    progress: { processing: i + batch.length, total: totalIds }
                });

                const response = await TrustAdminApiFactory().listsOwners.getListsOwners({
                    ids: batch
                });
                response.data.customerIds.forEach((customerId) => allCustomerIds.add(customerId));
                allErrors.push(...response.data.errors);
            }

            // sort so it is easier to unit test
            const uniqueCustomerIds = [...allCustomerIds].sort();

            setResponseData({ fetching: false });

            navigate('/lists/results', {
                state: {
                    successMessage: 'Customer and List Ids submitted successfully!',
                    listsOwners: { customerIds: uniqueCustomerIds, errors: allErrors }
                }
            });
        } catch (e) {
            setResponseData({ error: e as AxiosError, fetching: false });
        }
    };

    return (
        <ListSpamWorkflowFormView
            inputData={inputData}
            onInputChange={onInputChange}
            responseData={responseData}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            onSubmit={onSubmit}
            validateInput={validateInput.bind(this, inputData)}
        />
    );
};
